import {
  VegaAccordion,
  VegaBox,
  VegaButton,
  VegaCard,
  VegaChip,
  VegaCodeBlock,
  VegaDivider,
  VegaFlex,
  VegaFont,
  VegaGrid,
} from '@globalpayments/vega-react';
import { format } from 'date-fns';
import { useState } from 'react';

import { getDate } from '../../configInfo/components/Functions';
import { formatCodeBlock } from '../../../utility/formatCodeBlock';
import { Card } from './CardProps';

interface Props {
  card: Card;
  onCardClick: (appUid: string) => void;
}

const Detail = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <VegaFlex direction="col" gap="size-4">
    <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
      {label}
    </VegaFont>
    <VegaFont as="span" variant="font-p2-short">
      {value || 'N/A'}
    </VegaFont>
  </VegaFlex>
);

const SessionCard: React.FC<Props> = ({ card, onCardClick }) => {
  const dateCreated = format(getDate(new Date(card.createdUTC)), 'MM/dd/yyyy, hh:mm:ssaaa');
  const dateLastUpdate = format(getDate(new Date(card.lastUpdatedUTC)), 'MM/dd/yyyy, hh:mm:ssaaa');
  const dateSessionExpired = format(getDate(new Date(card.sessionExpirationUTC)), 'MM/dd/yyyy, hh:mm:ssaaa');
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);

  return (
    <VegaCard data-testid="application-result-card" padding="size-16" variant="border">
      <VegaFlex direction="col" gap="size-16">
        <VegaFlex alignItems="center" flexWrap="wrap" gap="size-12" justifyContent="space-between">
          <VegaFlex direction="col" gap="size-4">
            <VegaFont as="h3" variant="font-h5">
              {card.email || 'Email unavailable'}
            </VegaFont>
            <VegaFont as="span" color="text-secondary" variant="font-p2-short">
              {card.configName || 'Config unavailable'}
            </VegaFont>
          </VegaFlex>
          <VegaFlex flexWrap="wrap" gap="size-8">
            <VegaChip
              chipType="status"
              size="extra-small"
              statusColor={card.isCompleted ? 'bg-status-success' : 'bg-status-info'}
              text={card.isCompleted ? 'Completed' : 'In progress'}
            />
            <VegaChip
              chipType="status"
              size="extra-small"
              statusColor={card.isDisqualified ? 'bg-status-error' : 'bg-status-success'}
              text={card.isDisqualified ? 'Disqualified' : 'Qualified'}
            />
            <VegaChip
              chipType="label"
              size="extra-small"
              text={String(card.latestStatus || 'Status unknown')}
              variant="outlined"
            />
          </VegaFlex>
        </VegaFlex>
        <VegaDivider />
        <VegaGrid column={{ default: 1, M: 2, L: 3 }} gap="size-16">
          <Detail label="Application UID" value={card.appUid} />
          <Detail label="Current phase" value={card.currentPhaseName} />
          <Detail label="Current subflow" value={card.currentSubflowName} />
          <Detail label="Created" value={dateCreated} />
          <Detail label="Last updated" value={dateLastUpdate} />
          <Detail label="Expires" value={dateSessionExpired} />
        </VegaGrid>
        <VegaAccordion
          accordionTitle="Additional application details"
          expand={showAdditionalDetails}
          expandIconAlign="right"
          onVegaExpand={(event) => setShowAdditionalDetails(event.detail)}
          showExpandIcon>
          <VegaBox padding={{ top: 'size-16' }} slot="content">
            <VegaFlex direction="col" gap="size-16">
              <VegaGrid column={{ default: 1, M: 2, L: 3 }} gap="size-16">
                <Detail label="OnDemand config ID" value={card.onDemandConfigId} />
                <Detail label="Current phase ID" value={card.currentPhaseId} />
                <Detail label="Current subflow ID" value={card.currentSubflowId} />
                <Detail label="AAID" value={`${card.aaid || 'N/A'} ${card.partnerName || ''}`.trim()} />
                <Detail label="Affiliate ID" value={card.affiliateId} />
                <Detail label="Email verdict" value={card.emailVerdict} />
                <Detail label="Email verification" value={card.isEmailVerified ? 'Verified' : 'Not verified'} />
                <Detail label="Marketing preference" value={card.isOptedOut ? 'Opted out' : 'Opted in'} />
                <Detail label="Lead conversion" value={card.isLeadConverted ? 'Converted' : 'Not converted'} />
              </VegaGrid>
              <Detail label="Current status details" value={card.latestStatusDetails} />
              <VegaFlex direction="col" gap="size-8">
                <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
                  Company name
                </VegaFont>
                <VegaCodeBlock readOnly showCopyButton value={formatCodeBlock(card.companyNameJson || 'N/A')} />
              </VegaFlex>
            </VegaFlex>
          </VegaBox>
        </VegaAccordion>
        <VegaFlex justifyContent="end">
          <VegaButton
            icon="fa-solid fa-arrow-right"
            label="View application details"
            onVegaClick={() => onCardClick(card.appUid)}
            size="small"
            variant="secondary"
          />
        </VegaFlex>
      </VegaFlex>
    </VegaCard>
  );
};

export default SessionCard;
